import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CostReviewParameters } from '../models/CostReviewParameters';
import { ActionNeededField, ActionNeededFieldChange } from '../models/ActionNeededFieldChange';
import { CostReviewEstimateType } from '../models/enum';
import { IOptionalWorkflowAndEstimateType } from '../models/IOptionalWorkflowAndEstimateType';
import { CommonDataService } from '../services/common-data.service';
import { ProjectDateChange } from '../models/ProjectDateChange';
import { CostReviewDatabaseRecord } from '../models/CostReviewDatabaseRecord';
import { ITaskBasedLaborSettingsModel } from '../models/ITaskBasedLaborSettingsModel';
import { createEmptyJobDetailsFields, IJobDetailsFields } from '../models/IJobDetailsFields';
import { Constants } from '../constants/constants';


@Injectable({
  providedIn: 'root'
})
export class ReviewDetailTabsService {
 
  private readonly reviewDetailsData$ = new BehaviorSubject<CostReviewDatabaseRecord>(new CostReviewDatabaseRecord());
  private readonly reviewParameterData$ = new BehaviorSubject<CostReviewParameters[]>(new Array<CostReviewParameters>());
  private readonly business$ = new BehaviorSubject<string>('');
  private readonly cultureString$ = new BehaviorSubject<string>('');
  private readonly subsegmentId$ = new BehaviorSubject<number | null>(null);
  private readonly _taskBasedLaborSettings$ = new BehaviorSubject<ITaskBasedLaborSettingsModel | null>(null);
  readonly taskBasedLaborSettings$ = this._taskBasedLaborSettings$.asObservable();
  private readonly jobDetailsDialogRequested$ = new Subject<void>();

  private _estimateSellPrice:number = 0;
  private jobDetailsProductTypes: readonly string[] = [];

  setJobDetailsProductTypes(values: readonly string[]): void {
    this.jobDetailsProductTypes = values;
  }

  getJobDetailsProductTypes(): readonly string[] {
    return this.jobDetailsProductTypes;
  }
  /**
   * Emits when a single action-needed-relevant field changes in the library.
   * The payload carries the field identifier and its new value so the app can
   * apply a targeted update instead of re-syncing the entire object.
   */
  private readonly actionNeededFieldChanged$ = new Subject<ActionNeededFieldChange>();

  /**
   * Emits when project dates change in the SOL summary tab (edit mode).
   * Independent from the action-needed channel.
   */
  private readonly projectDateChanged$ = new Subject<ProjectDateChange>();

  /**
   * Tracks whether there are unsaved form changes in the library tabs.
   * The app subscribes to this to enable/disable save functionality.
   */
  private readonly unsavedChanges$ = new BehaviorSubject<boolean>(false);

  /**
   * Emits when the app needs the currently active tab (if any) to push its
   * latest, unsaved form data into the shared cost review object right away,
   * without waiting for the tab to be switched/destroyed (e.g. before Save or
   * Cost Review Request is executed while the tab stays open).
   */
  private readonly reviewFormDataSyncRequested$ = new Subject<void>();

   
  private optionalWorkflowAndEstimateType: IOptionalWorkflowAndEstimateType = { optionalWorkflow: false, optionalEstimateType: CostReviewEstimateType.None };

  private reviewLibraryDisableFlag: boolean = false;
  private isLibraryUsedInSQ: boolean = false;

  constructor(
    private readonly commonDataService: CommonDataService
  ) { }

  // Getter and Setter for ReviewData and Parameter Data Mode
  get getReviewDetailsData$(): Observable<CostReviewDatabaseRecord> {
    return this.reviewDetailsData$.asObservable();
  }

  get getReviewParameterData$(): Observable<CostReviewParameters[]> {
    return this.reviewParameterData$.asObservable();
  }


  get getBusiness$(): Observable<string> {
    return this.business$.asObservable();
  }

  get getCultureString$(): Observable<string> {
    return this.cultureString$.asObservable();
  }

  get getSubsegmentId$(): Observable<number | null> {
    return this.subsegmentId$.asObservable();
  }

  get estimateSellPrice(): number {
    return this._estimateSellPrice;
  }

  setBusiness(value: string): void {
    this.business$.next(value);
  }

  /**
   * Observable the app subscribes to for receiving field-specific
   * changes to action-needed-relevant data.
   */
  get getActionNeededFieldChanged$(): Observable<ActionNeededFieldChange> {
    return this.actionNeededFieldChanged$.asObservable();
  }

  /**
   * Observable the app subscribes to for receiving project date changes.
   * Independent from the action-needed channel.
   */
  get getProjectDateChanged$(): Observable<ProjectDateChange> {
    return this.projectDateChanged$.asObservable();
  }

  /**
   * Observable the app subscribes to for tracking unsaved changes in library tabs.
   */
  get getHasUnsavedChanges$(): Observable<boolean> {
    return this.unsavedChanges$.asObservable();
  }

  /**
   * Observable that the currently mounted tab component(s) subscribe to
   * (scoped with takeUntil(destroy$)) so they can push their latest form
   * data on demand.
   */
  get getReviewFormDataSyncRequested$(): Observable<void> {
    return this.reviewFormDataSyncRequested$.asObservable();
  }

  /**
   * Call this from the app before an operation that reads the cost review
   * object (e.g. Save, Cost Review Request) so any active-but-not-switched
   * tab pushes its latest changes into the shared cost review object first.
   */
  requestReviewFormDataSync(): void {
    this.reviewFormDataSyncRequested$.next();
  }

  /**
   * Called by the TrackChangesDirective when a form control is modified.
   */
  markAsUnsavedChanges(): void {
    if (!this.unsavedChanges$.getValue()) {
      this.unsavedChanges$.next(true);
    }
  }

  /**
   * Resets the unsaved changes flag. Call this from the app after saving.
   */
  resetUnsavedChanges(): void {
    this.unsavedChanges$.next(false);
  }

  setReviewDetailsData(value: CostReviewDatabaseRecord): void {
    this.reviewDetailsData$.next(value);
  }

  setReviewParameterData(value: CostReviewParameters[]): void {
    this.reviewParameterData$.next(value);
  }

  setDecimalPrecision(value: number): void {
    this.commonDataService.setDecimalPrecision(value);
  }

  setEstimateSellPrice(value: number): void {
    this._estimateSellPrice = value;
  }

  /**
   * Notifies the app that a single action-needed-relevant field has changed.
   *
   * Call this from tab components after modifying one of the 13 tracked fields
   * on `costReviewDetails` so the app can apply a pinpoint update and
   * re-evaluate action-needed visibility.
   *
   * @param field  The `ActionNeededField` that was modified.
   * @param value  The new value of that field.
   */
  notifyActionNeededFieldChanged(field: ActionNeededField, value: string | number | boolean): void {
    this.actionNeededFieldChanged$.next({ field, value });
  }

  /**
   * Notifies the app that project dates were changed in the summary tab.
   */
  notifyProjectDateChanged(dates: ProjectDateChange): void {
    this.projectDateChanged$.next(dates);
  }

   setOptionalWorkflowAndEstimateType(data: IOptionalWorkflowAndEstimateType): void {
    this.optionalWorkflowAndEstimateType = data;
  }

  getOptionalWorkflowAndEstimateType(): IOptionalWorkflowAndEstimateType {
    return this.optionalWorkflowAndEstimateType;
  }

  /**
   * Push a new value into the library's single read-only flag.
   * The app's `ReviewDetailComponent` is the sole intended writer — it
   * combines the app's read-only flag with the cost-review status
   * (`appFlag OR statusIsRead`) and calls this setter.
   */
  setReadOnlyMode(readOnly: boolean): void {
    this.commonDataService.setLibraryReadOnlyMode(readOnly);
    // Re-emit current data to trigger tab refresh so tabs pick up the new read mode
    this.reviewDetailsData$.next(this.reviewDetailsData$.getValue());
  }

  setUserCultureString(cultureString: string): void {
    if(cultureString) {
      this.cultureString$.next(cultureString);
    }
  }

  getUserCultureString(): string {
    return this.cultureString$.getValue();
  }

  setSubsegmentId(subsegmentId: number): void {
    this.subsegmentId$.next(subsegmentId);
  }

  getSubsegmentId(): number | null {
    return this.subsegmentId$.getValue();
  }

  setReviewLibraryDisableFlag(isDisabled: boolean): void {
    this.reviewLibraryDisableFlag = isDisabled;
  }

  getReviewLibraryDisableFlag(): boolean {
    return this.reviewLibraryDisableFlag;
  }
  
   setIsLibraryUsedInSQ(value: boolean): void {
    this.isLibraryUsedInSQ = value;
  }

  getIsLibraryUsedInSQ(): boolean {
    return this.isLibraryUsedInSQ;
  }
  getTaskBasedLaborSettings(): ITaskBasedLaborSettingsModel | null {
    return this._taskBasedLaborSettings$.value;
  }

  setTaskBasedLaborSettings(value: ITaskBasedLaborSettingsModel | null): void {
    if (this._taskBasedLaborSettings$.value === value) {
      return;
    }
    this._taskBasedLaborSettings$.next(value);
  }

  get getJobDetailsDialogRequested$(): Observable<void> {
    return this.jobDetailsDialogRequested$.asObservable();
  }

  requestJobDetailsDialog(): void {
    this.jobDetailsDialogRequested$.next();
  }

  updateTaskBasedLaborSettingsData(patchValue: Partial<ITaskBasedLaborSettingsModel>):boolean {
    const currentSettings = this._taskBasedLaborSettings$.value;
    if(!currentSettings) {
      return false;
    }
    const merged : ITaskBasedLaborSettingsModel = { ...currentSettings, ...patchValue };
    this._taskBasedLaborSettings$.next(merged);
    return true;
  }

  mapToJobDetailsFields(settings: ITaskBasedLaborSettingsModel | null): IJobDetailsFields {
    if(settings === null) {
      return createEmptyJobDetailsFields();
    }
    const isFireSprinklerOfferingSelected = settings?.isFireSprinklerOfferingSelected ?? false;
    const isFireAlarmOfferingSelected = settings?.isFireAlarmOfferingSelected ?? false;
    const isGasSystemOfferingSelected = settings?.isGasSystemOfferingSelected ?? false;
    const isERCESOfferingSelected = settings?.isERCESOfferingSelected ?? false;

    const {
      isSiteSizeQtyEnabled,
      isAntennaQtyEnabled,
      isAgentWeightEnabled,
      isCleanAgentNetVolEnabled } = this.setStateOfJobDetailsInputFields(isFireSprinklerOfferingSelected, isFireAlarmOfferingSelected, isGasSystemOfferingSelected, isERCESOfferingSelected);

    return {
      fireAlarmOffering: isFireAlarmOfferingSelected,
      fireSprinklerOffering: isFireSprinklerOfferingSelected,
      gaseousSystemOffering: isGasSystemOfferingSelected,
      ercesOffering: isERCESOfferingSelected,
      installationType: (settings?.selectedProductTypeValue) ?? Constants.EMPTY_STRING,
      siteSize: settings?.fireJobDetailsSiteSize ?? Constants.ZERO,
      antennaQty: settings?.fireJobDetailsAntennaQty ?? Constants.ZERO,
      agentWeight: settings?.fireJobDetailsAgentWeight ?? Constants.ZERO,
      cleanAgentNetVolSpace: settings?.fireJobDetailsCleanAgentVolSpace ?? Constants.ZERO,
      isSiteSizeEnabled: isSiteSizeQtyEnabled,
      isAntennaQtyEnabled: isAntennaQtyEnabled,
      isAgentWeightEnabled: isAgentWeightEnabled,
      isCleanAgentNetVolSpaceEnabled: isCleanAgentNetVolEnabled
    };
  }

  setStateOfJobDetailsInputFields(isFireSprinklerOfferingSelected: boolean, isFireAlarmOfferingSelected: boolean, isGasSystemOfferingSelected: boolean, isERCESOfferingSelected: boolean) {
    const isSiteSizeQtyEnabled = isFireAlarmOfferingSelected || isFireSprinklerOfferingSelected || isGasSystemOfferingSelected || isERCESOfferingSelected;
    const isAntennaQtyEnabled = isERCESOfferingSelected;
    const isAgentWeightEnabled = isGasSystemOfferingSelected;
    const isCleanAgentNetVolEnabled = isGasSystemOfferingSelected;

    return { isSiteSizeQtyEnabled, isAntennaQtyEnabled, isAgentWeightEnabled, isCleanAgentNetVolEnabled };
  }
}